import { useTranslation } from "react-i18next";
import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Table,
  Button,
  Badge,
} from "reactstrap";
import { useHistory } from "react-router-dom";

import {
  ChapterDto,
  ChapterStatsDto,
  LessonStatsDto,
} from "../../interfaces/chapter";
import DeleteModal from "../../components/Common/DeleteModal";
import { AddEditChapterModal } from "./AddEditChapterModal";
import { useDispatch, useSelector } from "react-redux";
import {
  deleteChapter,
  deleteChapterConsent,
  deleteLesson,
  deleteLessonConsent,
  getChaptersStats,
  toggleChapterState,
  toggleLessonState,
} from "../../store/actions";
import { LessonDto, State, UserProfileDto } from "../../interfaces";
import { AddEditLessonModal } from "./AddEditLessonModal";
import { NoResultFound } from "../../components/Common";
import { TableSpinner } from "../../components";
import { RoleEnum } from "../../enums";

type TProps = {
  subjectId: string;
  onViewQuestions: (chapter: ChapterStatsDto, lesson: LessonStatsDto) => void;
};
export const QuestionStatisticsPage: React.FC<TProps> = ({
  subjectId,
  onViewQuestions,
}) => {
  //#region hooks
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  //#endregion

  //#region redux state
  const { loading: chaptersStatsLoading, response: chaptersStatsData } =
    useSelector(
      (state: any): State<ChapterStatsDto[]> => state.chapter.chaptersStats
    );

  useSelector((state: any): State<any> => state.chapter.toggleLessonState);

  const {
    loading: deleteChapterLoading,
    error: deleteChapterError,
    success: deleteChapterSuccess,
  } = useSelector(
    (state: any): State<ChapterDto> => state.chapter.deleteChapter
  );

  const {
    loading: deleteLessonLoading,
    error: deleteLessonError,
    success: deleteLessonSuccess,
  } = useSelector((state: any): State<LessonDto> => state.lesson.deleteLesson);

  const { response: profile } = useSelector(
    (state: any): State<UserProfileDto> => state.account.userProfile
  );
  //#endregion

  //#region internal state
  const [deletingChapterId, setDeletingChapterId] = useState<string | null>(
    null
  );
  const [showChapterDeleteModal, setShowChapterDeleteModal] =
    useState<boolean>(false);
  const [editingChapterId, setEditingChapterId] = useState<string | null>(null);
  const [showChapterAddEditModal, setShowChapterAddEditModal] =
    useState<boolean>(false);
  const [showLessonDeleteModal, setShowLessonDeleteModal] =
    useState<boolean>(false);
  const [deletingLessonId, setDeletingLessonId] = useState<string | null>(null);
  const [showLessonAddEditModal, setShowLessonAddEditModal] =
    useState<boolean>(false);
  const [editingLessonId, setEditingLessonId] = useState<string | null>(null);
  const [addingLessonChapterId, setAddingLessonChapterId] = useState<
    string | null
  >(null);
  //#endregion

  document.title = `${t("documentTitle", { pageName: "questions" })}`;

  //#region effects
  useEffect(() => {
    dispatch(getChaptersStats(subjectId, history));
  }, [subjectId]);

  useEffect(() => {
    if (!deleteLessonLoading && deleteLessonSuccess) {
      setDeletingLessonId(null);
      setShowLessonDeleteModal(false);
      dispatch(getChaptersStats(subjectId, history));
    }
  }, [deleteLessonLoading]);

  useEffect(() => {
    if (!deleteChapterLoading && deleteChapterSuccess) {
      setDeletingChapterId(null);
      setShowChapterDeleteModal(false);
      dispatch(getChaptersStats(subjectId, history));
    }
  }, [deleteChapterLoading]);

  //#endregion

  //#region handlers

  const onAddChapter = () => {
    setShowChapterAddEditModal(true);
  };

  const onEditChapter = (id: string) => {
    setEditingChapterId(id);
    setShowChapterAddEditModal(true);
  };

  const onToggleChapterState = (id: string) => {
    dispatch(toggleChapterState(id, history));
  };

  const onToggleLessonState = (chapterId: string, lessonId: string) => {
    dispatch(toggleLessonState(chapterId, lessonId));
  };

  const onDeleteChapter = (id: string) => {
    setDeletingChapterId(id);
    setShowChapterDeleteModal(true);
    dispatch(deleteChapterConsent(id));
  };

  const onDeleteChapterConfirm = () => {
    dispatch(deleteChapter(deletingChapterId as string, history));
    setDeletingChapterId(null);
  };

  const onAddLesson = (chapterId: string) => {
    setAddingLessonChapterId(chapterId);
    setShowLessonAddEditModal(true);
  };

  const onEditLesson = (id: string, chapterId: string) => {
    setAddingLessonChapterId(chapterId);
    setEditingLessonId(id);
    setShowLessonAddEditModal(true);
  };

  const onDeleteLesson = (id: string) => {
    setDeletingLessonId(id);
    setShowLessonDeleteModal(true);
    dispatch(deleteLessonConsent(id));
  };

  const onDeleteLessonConfirm = () => {
    dispatch(deleteLesson(deletingLessonId as string, history));
    setDeletingLessonId(null);
  };

  //#endregion

  return (
    <React.Fragment>
      <Container fluid>
        <Row className="mt-2 mb-4 align-items-center">
          <Col className="col-sm-auto">
            <div>
              <h4 className="mb-0">{t("questions")}</h4>
            </div>
          </Col>
          <Col className="col-sm">
            <div className="d-flex justify-content-sm-end gap-2">
              <Button color="success" onClick={() => onAddChapter()}>
                <i className="ri-add-fill me-1 align-bottom"></i>
                {t("addChapter")}
              </Button>
            </div>
          </Col>
        </Row>

        {chaptersStatsLoading && <TableSpinner />}
        {!chaptersStatsLoading && chaptersStatsData && (
          <Row>
            {chaptersStatsData.map((item: ChapterStatsDto, key) => (
              <Col lg={12} key={key}>
                <Card>
                  <CardBody>
                    <Row className="g-1 mb-3">
                      <div className="table-responsive">
                        <Table className="table-bordered table-nowrap align-middle mb-0">
                          <thead>
                            <tr style={{ border: "none" }}>
                              <th
                                colSpan={5}
                                scope="col"
                                className="text-left fs-16"
                                style={{ border: "none" }}
                              >
                                {i18n.language === "ar"
                                  ? item.name.ar
                                  : item.name.en}{" "}
                                &nbsp;
                                {item.sequenceNumber &&
                                  item.sequenceNumber > 0 && (
                                    <Badge color={`${"secondary"}`}>
                                      {item.sequenceNumber}
                                    </Badge>
                                  )}
                              </th>

                              <th
                                scope="col"
                                style={{
                                  border: "none",
                                  textAlign:
                                    i18n.language === "ar" ? "left" : "right",
                                }}
                              >
                                {profile &&
                                  profile.role.key == RoleEnum.SUPER_ADMIN && (
                                    <Button
                                      className={`btn btn-sm ${
                                        item.isActive ? "btn-danger" : ""
                                      }`}
                                      onClick={() =>
                                        onToggleChapterState(item.id)
                                      }
                                    >
                                      {item.isActive
                                        ? t("inactivate")
                                        : t("activate")}
                                    </Button>
                                  )}
                                &nbsp;
                                <Button
                                  title={t("editItem", {
                                    item: "chapter",
                                  })}
                                  className="btn btn-sm btn-success"
                                  onClick={() => onEditChapter(item.id)}
                                >
                                  {t("edit")}
                                </Button>
                                &nbsp;
                                <Button
                                  title={t("deleteItem", { item: "chapter" })}
                                  className="btn btn-sm btn-danger"
                                  onClick={() => onDeleteChapter(item.id)}
                                >
                                  {t("delete")}
                                </Button>
                              </th>
                            </tr>
                          </thead>
                          <thead>
                            <tr>
                              <th
                                scope="col"
                                className="text-center cursor-none"
                              >
                                {t("models")} &nbsp;
                                {i18n.language === "ar" ? (
                                  <i className="ri-arrow-left-line align-bottom"></i>
                                ) : (
                                  <i className="ri-arrow-right-line align-bottom"></i>
                                )}
                              </th>
                              <th
                                scope="col"
                                className="text-center table-success"
                              >
                                D
                              </th>
                              <th
                                scope="col"
                                className="text-center table-success"
                              >
                                C
                              </th>
                              <th
                                scope="col"
                                className="text-center table-success"
                              >
                                B
                              </th>
                              <th
                                scope="col"
                                className="text-center table-success"
                              >
                                A
                              </th>
                              <th scope="col" className="text-center">
                                {t("lessons")} &nbsp;
                                <i className="ri-arrow-down-line align-bottom"></i>
                              </th>
                            </tr>
                          </thead>
                          <thead>
                            <tr>
                              <th scope="col" className="text-center">
                                {t("totalQuestions")} &nbsp;
                                {i18n.language === "ar" ? (
                                  <i className="ri-arrow-left-line align-bottom"></i>
                                ) : (
                                  <i className="ri-arrow-right-line align-bottom"></i>
                                )}
                              </th>
                              <th
                                scope="col"
                                className="text-center table-secondary"
                              >
                                {item.lessons
                                  .map(
                                    (lessonItemTotal: LessonStatsDto) =>
                                      lessonItemTotal.modelDQuestionsCount
                                  )
                                  .reduce((accumulator, currentValue) => {
                                    return accumulator + currentValue;
                                  }, 0)}
                              </th>
                              <th
                                scope="col"
                                className="text-center table-secondary"
                              >
                                {item.lessons
                                  .map(
                                    (lessonItemTotal: LessonStatsDto) =>
                                      lessonItemTotal.modelCQuestionsCount
                                  )
                                  .reduce((accumulator, currentValue) => {
                                    return accumulator + currentValue;
                                  }, 0)}
                              </th>
                              <th
                                scope="col"
                                className="text-center table-secondary"
                              >
                                {item.lessons
                                  .map(
                                    (lessonItemTotal: LessonStatsDto) =>
                                      lessonItemTotal.modelBQuestionsCount
                                  )
                                  .reduce((accumulator, currentValue) => {
                                    return accumulator + currentValue;
                                  }, 0)}
                              </th>
                              <th
                                scope="col"
                                className="text-center table-secondary"
                              >
                                {item.lessons
                                  .map(
                                    (lessonItemTotal: LessonStatsDto) =>
                                      lessonItemTotal.modelAQuestionsCount
                                  )
                                  .reduce((accumulator, currentValue) => {
                                    return accumulator + currentValue;
                                  }, 0)}
                              </th>
                              <th
                                onClick={() => onAddLesson(item.id)}
                                scope="col"
                                className="text-center table-secondary cursor-pointer"
                              >
                                <i className="ri-add-fill me-1 align-bottom"></i>
                                <a title={t("addItem", { item: "lesson" })}>
                                  {t("addItem", { item: "lesson" })}
                                </a>
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {item.lessons.map((lessonItem, lessonKey) => (
                              <tr key={lessonKey}>
                                <td className="fw-medium text-center table-secondary cursor-pointer">
                                  <a
                                    title={t("viewQuestions")}
                                    onClick={() =>
                                      onViewQuestions(item, lessonItem)
                                    }
                                  >
                                    {t("viewQuestions")}
                                  </a>
                                </td>
                                <td
                                  className={`fw-medium text-center ${
                                    lessonItem.isModelDQuestionSkillsCountLesser &&
                                    profile?.role.key == RoleEnum.SUPER_ADMIN
                                      ? "bg-danger"
                                      : ""
                                  }`}
                                >
                                  {lessonItem.modelDQuestionsCount}
                                </td>
                                <td
                                  className={`fw-medium text-center ${
                                    lessonItem.isModelCQuestionSkillsCountLesser &&
                                    profile?.role.key == RoleEnum.SUPER_ADMIN
                                      ? "bg-danger"
                                      : ""
                                  }`}
                                >
                                  {lessonItem.modelCQuestionsCount}
                                </td>
                                <td
                                  className={`fw-medium text-center ${
                                    lessonItem.isModelBQuestionSkillsCountLesser &&
                                    profile?.role.key == RoleEnum.SUPER_ADMIN
                                      ? "bg-danger"
                                      : ""
                                  }`}
                                >
                                  {lessonItem.modelBQuestionsCount}
                                </td>
                                <td
                                  className={`fw-medium text-center ${
                                    lessonItem.isModelAQuestionSkillsCountLesser &&
                                    profile?.role.key == RoleEnum.SUPER_ADMIN
                                      ? "bg-danger"
                                      : ""
                                  }`}
                                >
                                  {lessonItem.modelAQuestionsCount}
                                </td>
                                <td className="fw-medium">
                                  <div
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                    }}
                                  >
                                    <span>
                                      {i18n.language === "ar"
                                        ? lessonItem.name.ar
                                        : lessonItem.name.en}
                                      &nbsp;
                                      {lessonItem.sequenceNumber &&
                                        lessonItem.sequenceNumber > 0 && (
                                          <Badge color={`${"secondary"}`}>
                                            {lessonItem.sequenceNumber}
                                          </Badge>
                                        )}
                                    </span>

                                    <div>
                                      {profile &&
                                        profile.role.key ==
                                          RoleEnum.SUPER_ADMIN && (
                                          <Button
                                            className={`btn btn-sm ${
                                              lessonItem.isActive
                                                ? "btn-danger"
                                                : ""
                                            }`}
                                            onClick={() =>
                                              onToggleLessonState(
                                                item.id,
                                                lessonItem.id
                                              )
                                            }
                                          >
                                            {lessonItem.isActive
                                              ? t("inactivate")
                                              : t("activate")}
                                          </Button>
                                        )}
                                      &nbsp;
                                      <Button
                                        title={t("editItem", {
                                          item: "lesson",
                                        })}
                                        className="btn btn-sm btn-success"
                                        onClick={() =>
                                          onEditLesson(
                                            lessonItem.id,
                                            lessonItem.id
                                          )
                                        }
                                      >
                                        {t("edit")}
                                      </Button>
                                      &nbsp;
                                      <Button
                                        title={t("deleteItem", {
                                          item: "lesson",
                                        })}
                                        className="btn btn-sm btn-danger"
                                        onClick={() =>
                                          onDeleteLesson(lessonItem.id)
                                        }
                                      >
                                        {t("delete")}
                                      </Button>
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    </Row>
                  </CardBody>
                </Card>
              </Col>
            ))}
          </Row>
        )}
        {!chaptersStatsLoading &&
          (!chaptersStatsData || chaptersStatsData.length == 0) && (
            <NoResultFound />
          )}
        <DeleteModal
          loading={deleteChapterLoading}
          show={showChapterDeleteModal}
          onDeleteClick={() => onDeleteChapterConfirm()}
          onCloseClick={() => {
            setShowChapterDeleteModal(false);
          }}
          error={deleteChapterError}
        />
        <AddEditChapterModal
          subjectId={subjectId}
          id={editingChapterId}
          show={showChapterAddEditModal}
          onCloseClick={() => {
            setShowChapterAddEditModal(false);
            dispatch(getChaptersStats(subjectId, history));
            setEditingChapterId(null);
          }}
        />
        <DeleteModal
          loading={deleteLessonLoading}
          show={showLessonDeleteModal}
          onDeleteClick={() => onDeleteLessonConfirm()}
          onCloseClick={() => {
            setShowLessonDeleteModal(false);
          }}
          error={deleteLessonError}
        />
        {addingLessonChapterId && (
          <AddEditLessonModal
            chapterId={addingLessonChapterId}
            id={editingLessonId}
            show={showLessonAddEditModal}
            onCloseClick={() => {
              setShowLessonAddEditModal(false);
              dispatch(getChaptersStats(subjectId, history));
              setEditingLessonId(null);
            }}
          />
        )}
      </Container>
    </React.Fragment>
  );
};
